<template>
  <div>
    <PendingDialog :show-dialog="formPending" />
    <ComplaintFormErrorSnackbar />

    <StepperElement
      :class="stepperClasses"
      :steps="steps"
      :show-buttons="false"
      :first-stepper-step="firstStepperStep"
      :set-step-to="formSuccessfulSubmitted ? 1 : undefined"
      @stepper-element::start="gotoTop"
      @stepper-element::change="gotoTop"
    />
  </div>
</template>

<script>
import { compact } from 'lodash-es'
import { mapGetters } from 'vuex'

import FORM_CONSTANTS from '@/js/components/Forms/ComplaintForm/_store-module/constants.js'
import FORM_GETTER from '@/js/components/Forms/ComplaintForm/_store-module/getter-types.js'

import ComplaintFormPathsMixin from '@/js/components/Forms/ComplaintForm/_mixins/complaint-form-paths-mixin'

import StepperElement from '@/js/components/Shared/StepperElement.vue'
import PendingDialog from '@/js/components/Shared/PendingDialog.vue'

import ComplaintFormErrorSnackbar from '@/js/components/Forms/ComplaintForm/ComplaintFormErrorSnackbar.vue'

// Shared Forms
import ComplaintFormUserData from '@/js/components/Forms/ComplaintForm/Steps/ComplaintFormUserData.vue'
import ComplaintFormSummary from '@/js/components/Forms/ComplaintForm/Steps/ComplaintFormSummary.vue'
import ComplaintFormConfirm from '@/js/components/Forms/ComplaintForm/Steps/ComplaintFormConfirm.vue'

// Engine Forms
import ComplaintFormImpulseProductData from '@/js/components/Forms/ComplaintForm/Steps/Engine/ComplaintFormImpulseProductData.vue'
import ComplaintFormImpulseErrorDesc from '@/js/components/Forms/ComplaintForm/Steps/Engine/ComplaintFormImpulseErrorDesc.vue'

// Worktime Forms
import ComplaintFormWorktimeProductData from '@/js/components/Forms/ComplaintForm/Steps/Worktime/ComplaintFormWorktimeProductData.vue'
import ComplaintFormWorktimeCompensation from '@/js/components/Forms/ComplaintForm/Steps/Worktime/ComplaintFormWorktimeCompensation.vue'

// Battery Forms
import ComplaintFormBatteryProductData from '@/js/components/Forms/ComplaintForm/Steps/Battery/ComplaintFormBatteryProductData.vue'
import ComplaintFormBatteryErrorDesc from '@/js/components/Forms/ComplaintForm/Steps/Battery/ComplaintFormBatteryErrorDesc.vue'

// Component Forms
import ComplaintFormComponentProductData from '@/js/components/Forms/ComplaintForm/Steps/Component/ComplaintFormComponentProductData.vue'
import ComplaintFormComponentErrorDescDefault from '@/js/components/Forms/ComplaintForm/Steps/Component/ComplaintFormComponentErrorDescDefault.vue'
import ComplaintFormComponentSparePartsSummary from '@/js/components/Forms/ComplaintForm/Steps/Component/ComplaintFormComponentSparePartsSummary.vue'

import ComplaintFormComponentTreeView from '@/js/components/Forms/ComplaintForm/Steps/Component/ComplaintFormComponentTreeView.vue'

export const COMPLAINT_FORM_START_STEP_INDEX = 2

export default {
  name: 'ComplaintFormStepper',
  components: {
    PendingDialog,
    ComplaintFormErrorSnackbar,
    StepperElement,
  },
  mixins: [ComplaintFormPathsMixin],
  data() {
    return {
      defaultComponentSteps: [
        {
          title: this.$t('complaintForm.componentsForm'),
          titleOnly: true,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.1'),
          component: ComplaintFormUserData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.2'),
          component: ComplaintFormComponentProductData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.3'),
          component: ComplaintFormComponentErrorDescDefault,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.4'),
          component: ComplaintFormSummary,
        },
      ],
      initialComponentSteps: [
        {
          title: this.$t('complaintForm.componentsForm'),
          titleOnly: true,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.1'),
          component: ComplaintFormUserData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.2'),
          component: ComplaintFormComponentProductData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.5'),
          component: ComplaintFormComponentTreeView,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.3'),
          component: ComplaintFormComponentSparePartsSummary,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.4'),
          component: ComplaintFormSummary,
        },
      ],
    }
  },
  computed: {
    ...mapGetters(FORM_CONSTANTS.STORE_ID, {
      formPending: FORM_GETTER.FORM_PENDING,
      formSuccessfulSubmitted: FORM_GETTER.FORM_SUCCESSFUL_SUBMITTED,
      skipSteps: FORM_GETTER.SKIP_STEPS,
      getBikeData: FORM_GETTER.BIKE_DATA,
    }),
    firstStepperStep() {
      return COMPLAINT_FORM_START_STEP_INDEX
    },
    buyEngineSteps() {
      return this.filterSteps([
        {
          title: this.$t('complaintForm.engineForm'),
          titleOnly: this.$featureApi.isVisible('fe-motor-purchase-test'),
          component: () =>
            import(
              /* webpackChunkName: "ComplaintFormImpulseProductDataTest" */ '@/js/components/Forms/ComplaintForm/Steps/Engine/ComplaintFormImpulseProductDataTest.vue'
            ),
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.1'),
          component: ComplaintFormUserData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.2'),
          component: ComplaintFormImpulseProductData,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.4'),
          component: ComplaintFormSummary,
        },
      ])
    },
    engineSteps() {
      import(
        /* webpackChunkName: "ComplaintFormImpulseProductDataTest" */ '@/js/components/Forms/ComplaintForm/Steps/Engine/ComplaintFormImpulseProductDataTest.vue'
      )
      return this.filterSteps([
        {
          title: this.$t('complaintForm.engineForm'),
          titleOnly: !this.$featureApi.isVisible('fe-motor-purchase-test'),
          component: () =>
            import(
              /* webpackChunkName: "ComplaintFormImpulseProductDataTest" */ '@/js/components/Forms/ComplaintForm/Steps/Engine/ComplaintFormImpulseProductDataTest.vue'
            ),
        },

        // {
        //   title: this.$t('complaintForm.engineForm'),
        //   titleOnly: !this.$featureApi.isVisible('fe-motor-purchase-test'),
        //   component: () =>
        //     import(
        //       /* webpackChunkName: "ComplaintFormImpulseProductDataTest" */ '@/js/components/Forms/ComplaintForm/Steps/Engine/ComplaintFormImpulseProductDataTest.vue'
        //     ),
        // },
        {
          title: this.$t('complaintForm.ticket.battery.progress.1'),
          component: ComplaintFormUserData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.2'),
          component: ComplaintFormImpulseProductData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.3'),
          component: ComplaintFormImpulseErrorDesc,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.4'),
          component: ComplaintFormSummary,
        },
      ])
    },
    worktimeSteps() {
      return this.filterSteps([
        {
          title: this.$t('complaintForm.submitReGreasingApplicationForm'),
          titleOnly: true,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.1'),
          component: ComplaintFormUserData,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.2'),
          component: ComplaintFormWorktimeProductData,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.3.worktime'),
          component: ComplaintFormWorktimeCompensation,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.4'),
          component: ComplaintFormSummary,
        },
      ])
    },
    batterySteps() {
      return this.filterSteps([
        {
          title: this.$t('complaintForm.batteryForm'),
          titleOnly: true,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.1'),
          component: ComplaintFormUserData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.2'),
          component: ComplaintFormBatteryProductData,
        },
        {
          title: this.$t('complaintForm.ticket.battery.progress.3'),
          component: ComplaintFormBatteryErrorDesc,
        },
        {
          title: this.$t('complaintForm.ticket.engine.progress.4'),
          component: ComplaintFormSummary,
        },
      ])
    },
    componentSteps() {
      return this.filterSteps(this.filteredComponentSteps)
    },
    filteredComponentSteps() {
      return Object.keys(this.getBikeData).length
        ? this.initialComponentSteps
        : this.defaultComponentSteps
    },
    confirmSteps() {
      return [
        {
          title: this.$t('complaintForm.confirmHeader'),
          component: ComplaintFormConfirm,
        },
      ]
    },
    steps() {
      if (this.formSuccessfulSubmitted) {
        return this.confirmSteps
      }

      switch (this.ticketType) {
        case FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.ENGINE: {
          if (this.isWorkingTimeRemunerationForm) {
            return this.worktimeSteps
          }

          return this.engineSteps
        }
        case FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.BUY_ENGINE: {
          return this.buyEngineSteps
        }
        case FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.BATTERY: {
          return this.batterySteps
        }
        case FORM_CONSTANTS.SOURCE_DATA_CONSTANTS.TICKET_TYPES.COMPONENT: {
          return this.componentSteps
        }
        default: {
          return []
        }
      }
    },
    stepperClasses() {
      return [
        this.$style.step,
        { [this.$style.overflow]: this.isEngineForm || this.isBuyEngineForm },
      ]
    },
  },
  methods: {
    gotoTop() {
      window.scrollTo(0, 0)
    },
    filterSteps(steps) {
      console.log('filterSteps', steps)
      if (steps.length > 0) {
        const filteredSteps = [...steps]
        // const hasTitleOnlyStep = !!steps[0].titleOnly
        // ToDo: fix me
        this.skipSteps.forEach(step => {
          // const index = hasTitleOnlyStep ? step : step - 1
          filteredSteps[step] = null
        })
        console.log('filterSteps', compact(filteredSteps))
        return compact(filteredSteps)
      }
    },
  },
}
</script>

<style lang="scss" module>
.overflow {
  overflow: visible;

  :global(.v-stepper__items) {
    overflow: visible;
  }
}

.step {
  :global(.v-stepper__content) {
    padding-top: 3.75rem;
    padding-bottom: 3.75rem;
  }
}
</style>
